import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { getAllRoles } from "../../../../../api/api";
import {
  addFinJobType,
  FIN_JOB_TYPE_MAPPING_COMPONENT,
  getAllFinJobTypes,
} from "../../../../../api/finJobTypeApi";
import { addPortalJobType } from "../../../../../api/portalJobTypesApi";
import { getFinansistFactStreams } from "../../../../../api/timesheetApi";
import { selectStyles } from "../../../../../utils/selectStyles";
import "./CreateJobType.css";

const TYPE_OF_FIN_JOB_TYPE_OPTIONS = [
  { value: "Проект", label: "Проект" },
  { value: "-1", label: "Без проекта" },
];

const CREATE_NEW_VALUE = "__create_new__";

const CREATE_NEW_OPTION = {
  value: CREATE_NEW_VALUE,
  label: "Создать новый",
};

function CreateJobType() {
  const [finJobTypes, setFinJobTypes] = useState([]);
  const [finJobTypesLoading, setFinJobTypesLoading] = useState(true);
  const [finJobTypesError, setFinJobTypesError] = useState("");

  const [factStreams, setFactStreams] = useState([]);
  const [factStreamsLoading, setFactStreamsLoading] = useState(false);
  const [factStreamsError, setFactStreamsError] = useState("");
  const [factStreamsLoaded, setFactStreamsLoaded] = useState(false);

  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [rolesError, setRolesError] = useState("");

  const [selectedFinJobType, setSelectedFinJobType] = useState(null);
  const [selectedFactStream, setSelectedFactStream] = useState(null);
  const [finJobTypeDirectoryName, setFinJobTypeDirectoryName] = useState("");
  const [savedFinJobTypeId, setSavedFinJobTypeId] = useState(null);
  const [savedFinJobTypeName, setSavedFinJobTypeName] = useState("");

  const [portalName, setPortalName] = useState("");
  const [typeOfFinJobType, setTypeOfFinJobType] = useState(
    TYPE_OF_FIN_JOB_TYPE_OPTIONS[1],
  );
  const [portalIsActive, setPortalIsActive] = useState(true);
  const [forAllRoles, setForAllRoles] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState([]);

  const [isSavingFinJobType, setIsSavingFinJobType] = useState(false);
  const [finJobSaveError, setFinJobSaveError] = useState("");
  const [finJobSaveSuccess, setFinJobSaveSuccess] = useState(false);

  const [isSubmittingPortal, setIsSubmittingPortal] = useState(false);
  const [portalSubmitError, setPortalSubmitError] = useState("");
  const [portalSuccess, setPortalSuccess] = useState(false);

  const isCreateNewMode = selectedFinJobType?.value === CREATE_NEW_VALUE;

  const finJobTypeSelectOptions = useMemo(() => {
    const existing = (finJobTypes || [])
      .filter((item) => item?.id != null && item?.name)
      .map((item) => ({
        value: item.id,
        label: item.name,
        raw: item,
      }))
      .sort((a, b) => a.label.localeCompare(b.label, "ru"));
    return [CREATE_NEW_OPTION, ...existing];
  }, [finJobTypes]);

  const reloadFinJobTypes = async () => {
    const list = await getAllFinJobTypes();
    setFinJobTypes(Array.isArray(list) ? list : []);
  };

  useEffect(() => {
    let cancelled = false;

    const loadFinJobTypes = async () => {
      setFinJobTypesLoading(true);
      setFinJobTypesError("");
      try {
        const list = await getAllFinJobTypes();
        if (cancelled) return;
        setFinJobTypes(Array.isArray(list) ? list : []);
      } catch (err) {
        if (!cancelled) {
          setFinJobTypesError(
            err.message || "Не удалось загрузить виды работ",
          );
        }
      } finally {
        if (!cancelled) {
          setFinJobTypesLoading(false);
        }
      }
    };

    loadFinJobTypes();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isCreateNewMode) return;
    if (factStreamsLoaded) return;

    let cancelled = false;

    const loadFactStreams = async () => {
      setFactStreamsLoading(true);
      setFactStreamsError("");
      try {
        const list = await getFinansistFactStreams();
        if (cancelled) return;
        const options = (Array.isArray(list) ? list : [])
          .filter((item) => item?.id != null && item?.name)
          .map((item) => ({
            value: item.id,
            label: item.name,
            raw: item,
          }))
          .sort((a, b) => a.label.localeCompare(b.label, "ru"));
        setFactStreams(options);
        setFactStreamsLoaded(true);
      } catch (err) {
        if (!cancelled) {
          setFactStreamsError(
            err.message || "Не удалось загрузить статьи Finansist",
          );
        }
      } finally {
        if (!cancelled) {
          setFactStreamsLoading(false);
        }
      }
    };

    loadFactStreams();
    return () => {
      cancelled = true;
    };
  }, [isCreateNewMode, factStreamsLoaded]);

  useEffect(() => {
    let cancelled = false;

    const loadRoles = async () => {
      setRolesLoading(true);
      setRolesError("");
      try {
        const data = await getAllRoles(null, true);
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        const options = list
          .filter((role) => role?.id != null && role?.name)
          .map((role) => ({
            value: role.id,
            label: role.name,
          }))
          .sort((a, b) => a.label.localeCompare(b.label, "ru"));
        setRoles(options);
      } catch (err) {
        if (!cancelled) {
          setRolesError(err.message || "Не удалось загрузить роли");
        }
      } finally {
        if (!cancelled) {
          setRolesLoading(false);
        }
      }
    };

    loadRoles();
    return () => {
      cancelled = true;
    };
  }, []);

  const clearFinJobSaveState = () => {
    setFinJobSaveError("");
    setFinJobSaveSuccess(false);
  };

  const handleFinJobTypeChange = (option) => {
    setSelectedFinJobType(option);
    clearFinJobSaveState();

    if (option?.value === CREATE_NEW_VALUE) {
      setSavedFinJobTypeId(null);
      setSavedFinJobTypeName("");
      setSelectedFactStream(null);
      setFinJobTypeDirectoryName("");
      return;
    }

    if (option?.value != null) {
      setSavedFinJobTypeId(Number(option.value));
      setSavedFinJobTypeName(option.label || "");
      setSelectedFactStream(null);
    }
  };

  const handleFactStreamChange = (option) => {
    setSelectedFactStream(option);
    setFinJobTypeDirectoryName(option?.label?.trim() || "");
    setSavedFinJobTypeId(null);
    setSavedFinJobTypeName("");
    clearFinJobSaveState();
  };

  const canSaveFinJobType =
    isCreateNewMode &&
    selectedFactStream &&
    finJobTypeDirectoryName.trim();

  const handleSaveFinJobType = async (e) => {
    e.preventDefault();
    setFinJobSaveError("");
    setFinJobSaveSuccess(false);

    if (!canSaveFinJobType) {
      setFinJobSaveError("Выберите статью Finansist и укажите имя в справочнике.");
      return;
    }

    setIsSavingFinJobType(true);
    try {
      const streamName = selectedFactStream.label;
      const streamId = Number(selectedFactStream.value);
      const directoryName = finJobTypeDirectoryName.trim();

      const created = await addFinJobType({
        name: directoryName,
        is_active: true,
        mappings: [
          {
            component: FIN_JOB_TYPE_MAPPING_COMPONENT,
            fact_stream_name: streamName,
            fact_stream_id: streamId,
            is_active: true,
          },
        ],
      });

      const newId = Number(created?.id);
      if (!Number.isFinite(newId) || newId <= 0) {
        throw new Error("Сервер не вернул id созданного вида работ");
      }

      await reloadFinJobTypes();

      const createdName = created?.name || directoryName;
      setSavedFinJobTypeId(newId);
      setSavedFinJobTypeName(createdName);
      setSelectedFinJobType({
        value: newId,
        label: createdName,
      });
      setFinJobSaveSuccess(true);
      setTimeout(() => setFinJobSaveSuccess(false), 4000);
    } catch (err) {
      setFinJobSaveError(err.message || "Не удалось сохранить в справочник");
    } finally {
      setIsSavingFinJobType(false);
    }
  };

  const resetPortalSection = () => {
    setPortalName("");
    setTypeOfFinJobType(TYPE_OF_FIN_JOB_TYPE_OPTIONS[1]);
    setPortalIsActive(true);
    setForAllRoles(false);
    setSelectedRoles([]);
    setPortalSubmitError("");
  };

  const isPortalFormValid = () => {
    if (!savedFinJobTypeId) return false;
    if (!portalName.trim()) return false;
    if (!forAllRoles && selectedRoles.length === 0) return false;
    return true;
  };

  const handleCreatePortalJobType = async (e) => {
    e.preventDefault();
    setPortalSubmitError("");

    if (!isPortalFormValid()) {
      setPortalSubmitError(
        "Сначала сохраните вид в справочнике, укажите имя на портале и роли.",
      );
      return;
    }

    setIsSubmittingPortal(true);
    try {
      await addPortalJobType({
        name: portalName.trim(),
        fin_job_type_id: savedFinJobTypeId,
        type_of_fin_job_type: typeOfFinJobType.value,
        for_all_roles: forAllRoles,
        role_ids: forAllRoles
          ? []
          : selectedRoles.map((role) => Number(role.value)),
        is_active: portalIsActive,
        department_id: null,
      });

      setPortalSuccess(true);
      resetPortalSection();
      setTimeout(() => setPortalSuccess(false), 4000);
    } catch (err) {
      setPortalSubmitError(
        err.message || "Не удалось создать вид работ на портале",
      );
    } finally {
      setIsSubmittingPortal(false);
    }
  };

  const portalSectionDisabled = !savedFinJobTypeId;

  return (
    <div className="create-job-type">
      {portalSuccess && (
        <div className="create-job-type__success" role="status">
          Вид работ на портале успешно создан
        </div>
      )}

      <section className="create-job-type__section">
        <h2 className="create-job-type__section-title">
          1. Справочник fin_job_type
        </h2>
        <p className="create-job-type__section-desc">
          Сначала выберите или создайте вид в справочнике и сохраните — понадобится
          id для привязки на портале.
        </p>

        {finJobSaveSuccess && (
          <div className="create-job-type__success" role="status">
            Сохранено в справочник (id: {savedFinJobTypeId}
            {savedFinJobTypeName ? `, «${savedFinJobTypeName}»` : ""})
          </div>
        )}

        <form className="create-job-type__form" onSubmit={handleSaveFinJobType}>
          <ul className="create-job-type__fields">
            <li className="create-job-type__field">
              <label className="create-job-type__label">Вид работ*</label>
              <Select
                options={finJobTypeSelectOptions}
                value={selectedFinJobType}
                onChange={handleFinJobTypeChange}
                styles={selectStyles}
                placeholder={
                  finJobTypesLoading ? "Загрузка..." : "Выберите или создайте новый"
                }
                isLoading={finJobTypesLoading}
                isDisabled={finJobTypesLoading || !!finJobTypesError || isSavingFinJobType}
                noOptionsMessage={() => "Виды работ не найдены"}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
              {finJobTypesError && (
                <span className="create-job-type__error">{finJobTypesError}</span>
              )}
            </li>

            {isCreateNewMode && (
              <>
                <li className="create-job-type__field">
                  <label className="create-job-type__label">
                    Статья Finansist*
                  </label>
                  <p className="create-job-type__hint">
                    По умолчанию имя в справочнике совпадает со статьёй Finansist,
                    но его можно изменить ниже.
                  </p>
                  <Select
                    options={factStreams}
                    value={selectedFactStream}
                    onChange={handleFactStreamChange}
                    styles={selectStyles}
                    placeholder={
                      factStreamsLoading
                        ? "Загрузка..."
                        : "Выберите статью из Finansist"
                    }
                    isLoading={factStreamsLoading}
                    isDisabled={
                      factStreamsLoading || !!factStreamsError || isSavingFinJobType
                    }
                    noOptionsMessage={() => "Статьи не найдены"}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                  {factStreamsError && (
                    <span className="create-job-type__error">{factStreamsError}</span>
                  )}
                </li>

                {selectedFactStream && (
                  <li className="create-job-type__field">
                    <label
                      className="create-job-type__label"
                      htmlFor="fin-job-type-directory-name"
                    >
                      Имя в справочнике*
                    </label>
                    <input
                      id="fin-job-type-directory-name"
                      type="text"
                      className="create-job-type__input"
                      value={finJobTypeDirectoryName}
                      onChange={(e) => {
                        setFinJobTypeDirectoryName(e.target.value);
                        clearFinJobSaveState();
                      }}
                      disabled={isSavingFinJobType}
                      placeholder="Имя вида работ в справочнике"
                    />
                  </li>
                )}
              </>
            )}

            {!isCreateNewMode && savedFinJobTypeId && (
              <li className="create-job-type__field">
                <span className="create-job-type__label">Выбран id</span>
                <p className="create-job-type__readonly-name">
                  {savedFinJobTypeId}
                  {savedFinJobTypeName ? ` — ${savedFinJobTypeName}` : ""}
                </p>
              </li>
            )}
          </ul>

          {finJobSaveError && (
            <p className="create-job-type__error create-job-type__error--submit">
              {finJobSaveError}
            </p>
          )}

          {isCreateNewMode && (
            <button
              type="submit"
              className="create-job-type__submit"
              disabled={isSavingFinJobType || !canSaveFinJobType}
            >
              {isSavingFinJobType ? "Сохранение..." : "Сохранить в справочник"}
            </button>
          )}
        </form>
      </section>

      <section
        className={`create-job-type__section create-job-type__section--portal${
          portalSectionDisabled ? " create-job-type__section--disabled" : ""
        }`}
      >
        <h2 className="create-job-type__section-title">
          2. Вид работ на портале
        </h2>
        <p className="create-job-type__section-desc">
          {portalSectionDisabled
            ? "Завершите шаг 1: выберите вид из списка или сохраните новый в справочник."
            : `Привязка к справочнику: id ${savedFinJobTypeId}${
                savedFinJobTypeName ? ` («${savedFinJobTypeName}»)` : ""
              }. Имя на портале задаётся отдельно.`}
        </p>

        <form className="create-job-type__form" onSubmit={handleCreatePortalJobType}>
          <fieldset
            className="create-job-type__fieldset"
            disabled={portalSectionDisabled || isSubmittingPortal}
          >
            <ul className="create-job-type__fields">
              <li className="create-job-type__field">
                <label
                  className="create-job-type__label"
                  htmlFor="portal-job-type-name"
                >
                  Имя на портале*
                </label>
                <p className="create-job-type__hint">
                  Как вид работ будет отображаться в портале (может отличаться от
                  Finansist и справочника).
                </p>
                <input
                  id="portal-job-type-name"
                  type="text"
                  className="create-job-type__input"
                  value={portalName}
                  onChange={(e) => setPortalName(e.target.value)}
                  placeholder="Например: Тест0001"
                />
              </li>

              <li className="create-job-type__field">
                <label className="create-job-type__label">
                  Тип привязки к проекту
                </label>
                <Select
                  options={TYPE_OF_FIN_JOB_TYPE_OPTIONS}
                  value={typeOfFinJobType}
                  onChange={setTypeOfFinJobType}
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </li>

              <li className="create-job-type__field create-job-type__field--checkbox">
                <label className="create-job-type__checkbox-label">
                  <input
                    type="checkbox"
                    checked={portalIsActive}
                    onChange={(e) => setPortalIsActive(e.target.checked)}
                  />
                  <span>Активен на портале</span>
                </label>
              </li>

              <li className="create-job-type__field create-job-type__field--checkbox">
                <label className="create-job-type__checkbox-label">
                  <input
                    type="checkbox"
                    checked={forAllRoles}
                    onChange={(e) => {
                      setForAllRoles(e.target.checked);
                      if (e.target.checked) {
                        setSelectedRoles([]);
                      }
                    }}
                  />
                  <span>Для всех ролей</span>
                </label>
              </li>

              {!forAllRoles && (
                <li className="create-job-type__field">
                  <label className="create-job-type__label">Роли*</label>
                  <Select
                    isMulti
                    options={roles}
                    value={selectedRoles}
                    onChange={setSelectedRoles}
                    styles={selectStyles}
                    placeholder={
                      rolesLoading
                        ? "Загрузка..."
                        : "Выберите одну или несколько ролей"
                    }
                    isLoading={rolesLoading}
                    isDisabled={rolesLoading || !!rolesError}
                    noOptionsMessage={() => "Роли не найдены"}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                  {rolesError && (
                    <span className="create-job-type__error">{rolesError}</span>
                  )}
                </li>
              )}
            </ul>
          </fieldset>

          {portalSubmitError && (
            <p className="create-job-type__error create-job-type__error--submit">
              {portalSubmitError}
            </p>
          )}

          <button
            type="submit"
            className="create-job-type__submit"
            disabled={
              portalSectionDisabled || isSubmittingPortal || !isPortalFormValid()
            }
          >
            {isSubmittingPortal ? "Создание..." : "Создать на портале"}
          </button>
        </form>
      </section>
    </div>
  );
}

export default CreateJobType;
